import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';

// Product key: same shape as applications.product (uppercase identifier, no dashes).
const ProductKeyField = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Z0-9][A-Z0-9_]*$/i, 'letters, digits or underscores (no dashes)');

// GitHub "owner/repo".
const RepoField = z
  .string()
  .trim()
  .min(3)
  .max(140)
  .regex(/^[\w.-]+\/[\w.-]+$/, 'must be owner/repo');

// Path within the Core repo, relative and without traversal — it is interpolated
// into a GitHub contents URL.
const VersionFileField = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[\w.-]+(\/[\w.-]+)*$/, 'must be a relative path')
  .refine((v) => !v.split('/').includes('..'), 'must not traverse directories');

// Name of the constant or JSON key holding the version.
const VersionKeyField = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[\w.$-]+$/, 'letters, digits, dot, dash or underscore');

export const VersionProductSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  displayName: z.string(),
  coreRepo: z.string(),
  versionFile: z.string(),
  versionKey: z.string(),
  siembraWorkflow: z.string(),
  tenantRepoPattern: z.string(),
  sortOrder: z.number().int(),
  enabled: z.boolean(),
  createdAt: IsoTimestampSchema,
});

export const CreateVersionProductSchema = z.object({
  key: ProductKeyField,
  displayName: z.string().trim().min(1).max(64),
  coreRepo: RepoField,
  versionFile: VersionFileField.optional(),
  versionKey: VersionKeyField.optional(),
  siembraWorkflow: z.string().trim().min(1).max(128).optional(),
  tenantRepoPattern: z.string().trim().min(1).max(128).optional(),
  sortOrder: z.number().int().optional(),
  enabled: z.boolean().optional(),
});

export const UpdateVersionProductSchema = z
  .object({
    displayName: z.string().trim().min(1).max(64).optional(),
    coreRepo: RepoField.optional(),
    versionFile: VersionFileField.optional(),
    versionKey: VersionKeyField.optional(),
    siembraWorkflow: z.string().trim().min(1).max(128).optional(),
    tenantRepoPattern: z.string().trim().min(1).max(128).optional(),
    sortOrder: z.number().int().optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
  });

export type VersionProduct = z.infer<typeof VersionProductSchema>;
export type CreateVersionProduct = z.infer<typeof CreateVersionProductSchema>;
export type UpdateVersionProduct = z.infer<typeof UpdateVersionProductSchema>;
